import { requiredValidator } from "components/Form/Form.utils";
import { Icon } from "components/Icon/Icon";
import { IconCollection } from "components/Icon/IconCollection";
import { IconWithTooltip } from "components/ObjectTemplateCanvas/components/components";
import { RelationshipConfig } from "config/config";
import {
    deleteActionSignature,
    DeleteListAction,
} from "containers/Resources/components/CommonActions/CommonActions";
import {
    isInvalidDraft,
    isReferenceDatatype,
    validateDefaultValue,
} from "config/resources/objectTemplateAttributeDefaultValue";
import {
    allowedValuesRowsToStored,
    normalizeAllowedValuesRows,
    validateAllowedValues,
} from "config/resources/objectTemplateAttributeAllowedValues";
import { isEqual } from "lodash";

const objectTemplateAttributeVariables = `
    $description: String,
    $reference_object_template_id: String,
    $reference_record_type: AllType,
    $is_nullable: Boolean,
    $is_unique: Boolean,
    $is_hidden: Boolean,
    $silent_update: Boolean,
    $searchable: Boolean,
    $default_value: SchemalessData,
    $allowed_values: SchemalessData,
`;
const objectTemplateAttributeQuery = `
    name: $name,
    description: $description,
    object_template_id: $object_template_id
    reference_object_template_id: $reference_object_template_id,
    reference_record_type: $reference_record_type,
    is_nullable: $is_nullable,
    is_unique: $is_unique,
    is_hidden: $is_hidden,
    silent_update: $silent_update,
    searchable: $searchable,
    default_value: $default_value,
    allowed_values: $allowed_values,
`;
const objectTemplateAttributeCreateQuery = `
    ${objectTemplateAttributeQuery}
    datatype: $datatype,
`;

const config: RelationshipConfig = {
    label: "Object template attribute",
    icon: IconCollection.cubeSend,
    resourceName: "object_template_attribute",
    resourceClass: "relationship",
    parentResourceName: "object_template",
    parentReferenceField: "object_template_id",
    parentReferenceName: "object_template_id_object_template.name",
    layout: [],
    hideCreateNew: true,
    labelDocs: {
        title: "Object Templates",
        urlPath: "/docs/object-templates-custom-objects#object-templates",
    },
    actions: {
        shown: [],
        hidden: [deleteActionSignature],
    },
    listActions: {
        shown: [],
        hidden: [{ key: "delete", action: DeleteListAction }],
    },
    graphql: {
        resource: "object_template_attribute",
        resourceBody: `
            id
            name
            description
            object_template_id
            object_template_id_object_template{
                name
                deleted_at
                is_readonly
            }
            datatype
            reference_object_template_id
            reference_record_type
            is_nullable
            is_unique
            is_hidden
            silent_update
            searchable
            default_value
            allowed_values
            sort_order
        `,
        createVariables: `
            $name: String!,
            $datatype: ObjectTemplateAttributeDataType!,
            $object_template_id: String!,
            ${objectTemplateAttributeVariables}
        `,
        createQuery: `
            ${objectTemplateAttributeCreateQuery}
        `,
        updateVariables: `
            $id: String!,
            $by: String,
            $name: String,
            $object_template_id: String,
            $sort_order: Int,
            ${objectTemplateAttributeVariables}
        `,
        updateQuery: `
            ${objectTemplateAttributeQuery}
            sort_order: $sort_order,
        `,
    },
    form: [
        {
            name: "name",
            component: "text",
            fieldProps: {
                label: "Name",
            },
            fieldAttr: {
                required: true,
            },
            validation: requiredValidator,
            isShownInCanvas: false,
        },
        {
            name: "description",
            component: "text",
            fieldProps: {
                label: "Description",
                isMultiLine: true,
            },
            fieldAttr: {
                required: false,
            },
            isShownInCanvas: false,
        },
        {
            name: "datatype",
            component: "autocomplete",
            fieldProps: {
                label: "Datatype",
                options: {
                    enumName: "ObjectTemplateAttributeDataType",
                },
                iconOnlyComponent: ({ hasValue, isDisabled }) => (
                    <IconWithTooltip
                        inner={<Icon icon={IconCollection.databaseEdit} />}
                        overlay={`datatype${hasValue ? `: ${hasValue}` : ""}`}
                        isDisabled={isDisabled}
                        align={{ offset: [-12, 25] }}
                    />
                ),
            },
            fieldAttr: {
                required: true,
                isChangeable: false,
            },
            validation: requiredValidator,
        },
        {
            name: "reference_object_template_id",
            component: "autocomplete",
            fieldProps: {
                label: "Reference object template",
                options: {
                    resource: "object_template",
                },
                iconOnlyComponent: ({ hasValue, isDisabled }) => (
                    <IconWithTooltip
                        inner={<Icon icon={IconCollection.objectTemplate} />}
                        overlay={`object template reference${hasValue ? `: ${hasValue}` : ""}`}
                        isDisabled={isDisabled}
                        align={{ offset: [-12, 25] }}
                    />
                ),
            },
            fieldAttr: {
                required: true,
                dependee: { fieldName: "datatype", fieldValue: "OBJECT_TEMPLATE_REFERENCE" },
            },
            validation: (value, values) => {
                if (values.datatype === "OBJECT_TEMPLATE_REFERENCE" && value) {
                    return undefined;
                } else {
                    return "Required.";
                }
            },
        },
        {
            name: "reference_record_type",
            component: "autocomplete",
            fieldProps: {
                label: "Reference record type",
                options: {
                    enumName: "AllType",
                    filterOptions: ({ value }) =>
                        value !== "OAUTH" && value !== "PROCESS" && value !== "TAG",
                },
                iconOnlyComponent: ({ hasValue, isDisabled }) => (
                    <IconWithTooltip
                        inner={<Icon icon={IconCollection.codeBraces} />}
                        overlay={`record reference${hasValue ? `: ${hasValue}` : ""}`}
                        isDisabled={isDisabled}
                        align={{ offset: [-12, 25] }}
                    />
                ),
            },
            fieldAttr: {
                required: true,
                dependee: { fieldName: "datatype", fieldValue: "RECORD_REFERENCE" },
            },
            validation: (value, values) => {
                if (values.datatype === "RECORD_REFERENCE" && value) {
                    return undefined;
                } else {
                    return "Required.";
                }
            },
        },
        {
            name: "allowed_values",
            // Edited with a compact "form-array" editor (a "+ add allowed value" button plus a
            // removable list of value/label rows) rather than raw YAML/JSON. The field value is
            // the structured list of { const, label } rows; (de)serialisation to the stored
            // { const, label? } shape lives in objectTemplateAttributeAllowedValues.ts.
            component: "allowedValuesEditor",
            isShownInCanvas: false,
            fieldProps: {
                label: "Allowed values",
                fieldNote: () => (
                    <span>
                        The options for this dropdown attribute. Each option has a{" "}
                        <strong>value</strong> (stored on the custom object; must be unique) and an
                        optional <strong>label</strong> (shown in the dropdown; falls back to the
                        value).
                    </span>
                ),
            },
            fieldAttr: {
                required: true,
                dependee: { fieldName: "datatype", fieldValue: "ALLOWED_VALUES" },
            },
            submitDto: (value: any) => allowedValuesRowsToStored(value),
            isEqual: (valueA: any, valueB: any) => isEqual(valueA, valueB),
            validation: (value: any, values: any) =>
                validateAllowedValues(value, values?.datatype),
        },
        {
            name: "is_nullable",
            component: "switch",
            fieldProps: {
                label: "Can be set to null",
                defaultValue: false,
                iconOnlyComponent: IconCollection.helpIcon,
                iconTooltipOverlay: "can be null",
            },
            fieldAttr: {
                required: true,
            },
            conditionalFieldProps: {
                condition: { fieldName: "datatype", fieldValue: "JSON" },
                fieldProps: { value: false, isReadOnly: true },
            },
        },
        {
            name: "is_unique",
            component: "switch",
            fieldProps: {
                label: "Is unique",
                defaultValue: false,
                iconOnlyComponent: IconCollection.snowFlake,
                iconTooltipOverlay: "unique",
            },
            fieldAttr: {
                required: true,
            },
        },
        {
            name: "is_hidden",
            component: "switch",
            fieldProps: {
                label: "Is hidden",
                defaultValue: false,
                iconOnlyComponent: IconCollection.eyeOutline,
                iconTooltipOverlay: "hidden",
            },
            fieldAttr: {
                required: true,
            },
        },
        {
            name: "silent_update",
            component: "switch",
            fieldProps: {
                label: "Silent update",
                defaultValue: false,
                iconOnlyComponent: IconCollection.bell,
                iconTooltipOverlay: "silent update",
            },
            fieldAttr: {
                required: true,
            },
        },
        {
            name: "searchable",
            component: "switch",
            fieldProps: {
                label: "Searchable",
                defaultValue: false,
                iconOnlyComponent: IconCollection.magnify,
                iconTooltipOverlay: "searchable",
                fieldNote: () => (
                    <span>
                        Include this attribute&apos;s value in the platform full-text search index of
                        its custom objects. Best suited to text attributes (STRING/TEXT/MARKDOWN).
                        Toggling re-indexes this object template&apos;s custom objects.
                    </span>
                ),
            },
            fieldAttr: {
                required: true,
            },
        },
        {
            name: "default_value",
            // Datatype-aware editor: renders the control matching the chosen
            // `datatype` (boolean switch, number input, date/time picker, allowed-
            // values dropdown, text, or the raw YAML/JSON editor for JSON/object
            // shapes) instead of a generic JSON editor, with nullable → clearable-
            // to-null semantics. The component flows in-document in the compact
            // attribute side-panel (no absolute/overlay positioning like the Monaco
            // code editor, which used to cover the Save button). It stores the
            // structured value directly, so no (de)serialisation is needed in
            // submitDto / formDTO — see DefaultValueEditor.
            component: "defaultValueEditor",
            // Not shown in the compact ObjectTemplateCanvas attribute card (it has
            // no icon-only representation); edited in the attribute side-panel form.
            isShownInCanvas: false,
            fieldProps: {
                label: "Default value",
                fieldNote: (values: any) => {
                    if (isReferenceDatatype(values?.datatype)) {
                        return (
                            <span>
                                Pick an existing record to use as the default reference. It is
                                applied at custom-object creation when this attribute is omitted, and
                                cannot be deleted while it is a live default. Use the clear button in
                                the picker to reset it to no default.
                            </span>
                        );
                    }
                    if (values?.is_nullable === false && values?.default_value == null) {
                        return (
                            <span>
                                ⚠ This attribute is required and has no default: creating a custom
                                object that omits this attribute will be rejected. Set a default here
                                (or make the attribute nullable) to avoid that.
                            </span>
                        );
                    }
                    return (
                        <span>
                            Applied at custom-object creation when this attribute is omitted. Use the
                            clear (trash) button to reset it to no default.
                        </span>
                    );
                },
            },
            fieldAttr: {
                required: false,
            },
            // The editor stores the correctly-typed structured value, so submit is a
            // pass-through — except an unparseable raw YAML/JSON draft (JSON datatype),
            // which is blocked here as a safety net in addition to field validation.
            submitDto: (value: any) => {
                if (isInvalidDraft(value)) {
                    throw new Error("Default value is not valid YAML/JSON");
                }
                return value;
            },
            isEqual: (valueA: any, valueB: any) => isEqual(valueA, valueB),
            validation: (value: any, values: any) =>
                validateDefaultValue(value, values?.datatype),
        },
    ],
    formDTO: {
        initialValueDTO: (data: any) => ({
            ...data,
            default_value: data?.default_value ?? null,
            allowed_values: normalizeAllowedValuesRows(data?.allowed_values),
        }),
    },
    parentData: {
        parentDeletedAt: (data: any) => data?.object_template_id_object_template?.deleted_at,
        isParentReadOnly: (data: any) => data?.object_template_id_object_template?.is_readonly,
    },
};
export default config;
